import React, { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FiX, FiMessageCircle, FiShoppingCart } from 'react-icons/fi';
import { apiFetch } from '../api';

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};

const modalStyle = {
  backgroundColor: '#ffffff',
  width: '95%',
  maxWidth: '480px',
  height: '80vh',
  maxHeight: '640px',
  borderRadius: '14px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
};

const transcriptStyle = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
};

const questionBubbleStyle = {
  alignSelf: 'flex-end',
  maxWidth: '85%',
  backgroundColor: '#0F766E',
  color: '#ffffff',
  borderRadius: '12px',
  padding: '8px 12px',
  fontSize: '0.95em',
  flexShrink: 0,
};

const answerBubbleStyle = {
  alignSelf: 'flex-start',
  maxWidth: '95%',
  backgroundColor: '#f1f5f9',
  color: '#0F172A',
  borderRadius: '12px',
  padding: '8px 12px',
  fontSize: '0.95em',
  whiteSpace: 'pre-wrap',
  flexShrink: 0,
};

// SHOP-110: overrides for react-markdown's rendered elements so bullets/bold
// inherit the answer bubble's existing color/font-size instead of the
// browser's default block margins and list indentation blowing out the
// bubble's compact spacing (answerBubbleStyle's 8px/12px padding above).
const markdownParagraphStyle = { margin: 0, color: '#0F172A', fontSize: '0.95em' };
const markdownListStyle = { margin: '4px 0', paddingLeft: '20px', color: '#0F172A', fontSize: '0.95em' };
const markdownListItemStyle = { margin: 0 };
const markdownStrongStyle = { fontWeight: 700, color: '#0F172A' };

const markdownComponents = {
  p: ({ node, ...props }) => <p style={markdownParagraphStyle} {...props} />,
  ul: ({ node, ...props }) => <ul style={markdownListStyle} {...props} />,
  li: ({ node, ...props }) => <li style={markdownListItemStyle} {...props} />,
  strong: ({ node, ...props }) => <strong style={markdownStrongStyle} {...props} />,
};

const itemsListStyle = {
  listStyle: 'none',
  margin: '8px 0 0',
  padding: 0,
  border: '1px solid #e5e7eb',
  borderRadius: '10px',
  overflow: 'hidden',
  alignSelf: 'flex-start',
  maxWidth: '95%',
  width: '100%',
  flexShrink: 0,
};

const itemRowStyle = {
  padding: '8px 12px',
  borderBottom: '1px solid #f1f5f9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

const itemRowInfoStyle = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
};

// SHOP-177: mirrors SubcategoryItemsModal's addToCartButtonStyle - same
// per-row add-to-cart affordance/icon used there.
const addToCartButtonStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#16a34a',
  padding: '6px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const formStyle = {
  display: 'flex',
  gap: '8px',
  padding: '12px 16px',
  borderTop: '1px solid #e5e7eb',
  flexShrink: 0,
};

const inputStyle = {
  flex: 1,
  padding: '10px 12px',
  borderRadius: '10px',
  border: '1px solid #e2e8f0',
  fontSize: '0.95em',
};

const sendButtonStyle = {
  padding: '10px 16px',
  borderRadius: '10px',
  border: 'none',
  backgroundColor: '#0F766E',
  color: '#ffffff',
  fontWeight: 600,
  cursor: 'pointer',
};

// SHOP-104: /copilot/ask's own Gemini call (Utils/CopilotAI.py) has no
// request-level timeout - a stalled network call to Vertex AI can hang
// indefinitely with no error ever surfacing server-side. Aborting client-side
// after this long bounds the wait so the user sees askError instead of a
// spinner that never resolves (found via live browser verification - a
// follow-up turn once stalled past this point with no backend response).
const REQUEST_TIMEOUT_MS = 45000;

// SHOP-102/103: AskCopilotModal calls POST /copilot/ask. `history` mirrors
// what that endpoint returns (Gemini's own serialized turns, opaque to this
// component - only round-tripped, never rendered directly); `turns` is this
// component's own question/answer/items list, built purely for display.
// Both are discarded and regenerated fresh whenever the modal reopens
// (ADR-004: a user's mid-conversation adjustment doesn't persist across
// conversations - a "conversation" is one modal-open, per the design doc).
const AskCopilotModal = ({ isOpen, onClose, translations, language, selectedStores, onSelectItem }) => {
  const [question, setQuestion] = useState('');
  const [turns, setTurns] = useState([]);
  const [history, setHistory] = useState([]);
  const [conversationId, setConversationId] = useState(() => crypto.randomUUID());
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const transcriptRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setTurns([]);
    setHistory([]);
    setQuestion('');
    setHasError(false);
    setConversationId(crypto.randomUUID());
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!transcriptRef.current) return;
    transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
  }, [turns, isLoading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedQuestion = question.trim();
    if (!trimmedQuestion || isLoading) return;

    setIsLoading(true);
    setHasError(false);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    apiFetch('/copilot/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question: trimmedQuestion,
        language,
        history,
        conversation_id: conversationId,
        selected_stores: selectedStores,
      }),
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) throw new Error('Copilot request failed');
        return res.json();
      })
      .then((data) => {
        setTurns((prev) => [
          ...prev,
          { question: trimmedQuestion, answer: data.answer, items: data.items || null },
        ]);
        setHistory(data.history || []);
        setQuestion('');
      })
      .catch((err) => {
        console.error('Error asking Copilot:', err);
        setHasError(true);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        setIsLoading(false);
      });
  };

  if (!isOpen) return null;

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3
            style={{
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '1.1em',
              fontWeight: 700,
            }}
          >
            <FiMessageCircle />
            {translations?.askCopilotTitle || 'Ask Copilot'}
          </h3>

          <button
            onClick={onClose}
            style={{ border: 'none', background: 'transparent', fontSize: '1.2em', cursor: 'pointer' }}
            aria-label={translations?.modalClose || 'Close'}
          >
            <FiX size={20} />
          </button>
        </div>

        <div ref={transcriptRef} style={transcriptStyle}>
          {turns.length === 0 && !isLoading && (
            <div style={{ color: '#6b7280', fontSize: '0.9em' }}>
              {translations?.askEmptyState || 'Ask me about prices and product availability across supermarkets.'}
            </div>
          )}

          {turns.map((turn, index) => (
            <React.Fragment key={index}>
              <div style={questionBubbleStyle}>{turn.question}</div>
              <div style={answerBubbleStyle}>
                <ReactMarkdown components={markdownComponents}>{turn.answer}</ReactMarkdown>
              </div>
              {turn.items && turn.items.length > 0 && (
                <ul style={itemsListStyle}>
                  {turn.items.map((item, itemIndex) => (
                    <li key={`${item.productid || item.item_id || item.name}-${itemIndex}`} style={itemRowStyle}>
                      <div style={itemRowInfoStyle}>
                        <div style={{ fontWeight: 600 }}>{item.name}</div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '0.85em',
                            color: '#6b7280',
                          }}
                        >
                          <span>
                            {[item.category_es, item.store].filter(Boolean).join(' · ')}
                          </span>
                          {item.precio != null && (
                            <span style={{ fontWeight: 600, color: '#374151' }}>
                              ${Number(item.precio).toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                      {onSelectItem && (
                        <button
                          type="button"
                          onClick={() => onSelectItem(item)}
                          aria-label={translations?.addToCart || 'Add to cart'}
                          title={translations?.addToCart || 'Add to cart'}
                          style={addToCartButtonStyle}
                        >
                          <FiShoppingCart size={18} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </React.Fragment>
          ))}

          {isLoading && (
            <div style={{ ...answerBubbleStyle, color: '#6b7280' }}>
              {translations?.askSending || 'Thinking...'}
            </div>
          )}

          {hasError && (
            <div style={{ color: '#dc2626', fontSize: '0.9em' }}>
              {translations?.askError || "Couldn't get an answer. Please try again."}
            </div>
          )}
        </div>

        <form style={formStyle} onSubmit={handleSubmit}>
          <input
            autoFocus
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={translations?.askPlaceholder || 'Type your question...'}
            disabled={isLoading}
            style={inputStyle}
          />
          <button
            type="submit"
            disabled={isLoading || !question.trim()}
            style={{ ...sendButtonStyle, opacity: isLoading || !question.trim() ? 0.6 : 1 }}
          >
            {translations?.askButtonLabel || 'Send'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskCopilotModal;
